import {
  ServiceStatus,
  TrendData,
  FavoriteService,
  MonitorGroup,
  UserProfile,
  MinimalUserProfile,
  RegisterTokenResponse,
} from "../Models/Types";
import { BackToAppDeepLink } from "../Routes";
import MockApiService from "./MockApiService";
import { isDemoSession, clearDemoSession } from "./DemoSession";

const BASE_URL = "./api";
const TIMEOUT_MS = 30000;

const apiFetch = async <T,>(
  endpoint: string,
  options: RequestInit = {},
  authOverride?: string,
): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const sessionTokenDev = localStorage.getItem("sessionTokenDev");
  const sessionToken = localStorage.getItem("sessionToken");

  const bearerDev = sessionTokenDev ? `BearerDev ${sessionTokenDev}` : null;
  const bearer = sessionToken ? `Bearer ${sessionToken}` : null;

  // Gli header vanno costruiti con la classe Headers, non con un oggetto letterale.
  // In un oggetto JS "Authorization" e "authorization" sono DUE chiavi distinte, ma
  // per il protocollo sono lo stesso header: il costruttore Headers le concatena con
  // ", ", per cui al server arrivava "Bearer <jwt>, " e lo split lato middleware
  // restituiva il token con una virgola in coda (JWT non decodificabile -> 401).
  // Con set() l'header viene scritto una volta sola.
  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // La credenziale esplicita (login) ha precedenza su quella in localStorage.
  const auth = authOverride || bearer || bearerDev;
  if (auth) {
    headers.set("authorization", auth);
  } else {
    headers.delete("authorization");
  }

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const isLoginValidation = endpoint.includes("/auth/oauth2/validate");

    if (response.status === 401 && !isLoginValidation) {
      // Il corpo va letto PRIMA di svuotare lo storage: contiene reason/email/
      // signature_valid prodotti dal middleware. Senza questo il logout è muto.
      const authError: any = await response.json().catch(() => ({}));

      localStorage.clear();

      const params = new URLSearchParams({
        reason: authError.reason || "unknown",
        email: authError.email || "",
        signature_valid: String(authError.signature_valid ?? ""),
      });
      window.location.href = `${BackToAppDeepLink}?${params.toString()}`;

      throw new Error(
        `Unauthorized (401): ${authError.reason || "Redirecting..."}`,
      );
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      let errorBody: any = null;
      try {
        errorBody = await response.json();
        errorMessage =
          errorBody.message || errorBody.error || response.statusText;
      } catch {
        /* ignore */
      }
      const apiError: any = new Error(
        `Errore API (${response.status}): ${errorMessage}`,
      );
      apiError.status = response.status;
      apiError.body = errorBody;
      throw apiError;
    }

    return response.status === 204
      ? (null as unknown as T)
      : await response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError")
      throw new Error("La richiesta è scaduta (Timeout 30s).");
    throw error;
  }
};

// --- AUTH & MONITORING ---

const ValidateOAuth2Token = async (
  accessToken: string,
): Promise<{ email: string }> => {
  clearDemoSession(); // a real authentication always ends any demo session
  try {
    // Il token va passato come terzo argomento (authOverride), NON dentro
    // options.headers: in quel modo finiva in un header duplicato e corrotto.
    const data = await apiFetch<{ email: string }>(
      "/auth/oauth2/validate",
      { method: "POST" },
      `Bearer ${accessToken}`,
    );
    if (data && data.email) {
      localStorage.setItem("loggedUserId", data.email);
      localStorage.setItem("sessionToken", accessToken);
    }
    return data;
  } catch (error) {
    localStorage.removeItem("loggedUserId");
    localStorage.removeItem("sessionToken");
    throw error;
  }
};

const StartUidLogin = async (
  uid: string,
  password: string,
): Promise<boolean> => {
  clearDemoSession(); // a real authentication always ends any demo session
  try {
    const response = await apiFetch<any>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ uid, password }),
    });
    if (response && response.jwt) {
      localStorage.setItem("sessionTokenDev", response.jwt);
      localStorage.setItem("loggedUserId", uid);
    }
    return true;
  } catch (error) {
    localStorage.removeItem("sessionTokenDev");
    localStorage.removeItem("loggedUserId");
    throw error;
  }
};

// --- USERS & NOTIFICATIONS ---

/**
 * Fetches the current logged user profile and notification preferences
 */
const GetMyProfile = async (): Promise<MinimalUserProfile> => {
  return apiFetch<MinimalUserProfile>("/users/me");
};

/**
 * Updates the 'allow_notifications' flag on the server
 */
const UpdateNotificationSettings = async (
  allowNotifications: boolean,
): Promise<{ success: boolean }> => {
  return apiFetch<{ success: boolean }>("/users/notifications", {
    method: "PUT",
    body: JSON.stringify({ allowNotifications }),
  });
};

/**
 * Sends the FCM device token to the server to associate it with the user
 */
const RegisterFCMToken = async (
  fcmToken: string,
): Promise<RegisterTokenResponse> => {
  return apiFetch<RegisterTokenResponse>("/users/register-token", {
    method: "POST",
    body: JSON.stringify({ fcmToken }),
  });
};

// --- MONITORING ---

const GetMonitoredServicesStatus = async (): Promise<ServiceStatus[]> => {
  return apiFetch<ServiceStatus[]>("/monitor_services/status");
};

const GetMonitoredServicesTrend = async (hours: number): Promise<TrendData> => {
  return apiFetch<TrendData>(`/monitor_services/calculated-trend`);
};

// --- FAVORITES ---

const SetIsFavorited = async (
  appId: number,
  favorited: boolean,
): Promise<{ success: boolean; current_favorites: FavoriteService[] }> => {
  return apiFetch(
    `/favorites/set?applicationId=${appId}&favorited=${favorited}`,
  );
};

const GetAllFavorited = async (): Promise<FavoriteService[]> => {
  return apiFetch<FavoriteService[]>("/favorites");
};

// --- GROUPS ---

const GetGroups = async (): Promise<MonitorGroup[]> => {
  return apiFetch<MonitorGroup[]>("/groups");
};

const CreateGroup = async (
  groupName: string,
  applicationIdsInGroup: string,
  threshold: number = 10
): Promise<MonitorGroup> => {
  return apiFetch<MonitorGroup>("/groups", {
    method: "POST",
    body: JSON.stringify({ groupName, applicationIdsInGroup, threshold }),
  });
};

const UpdateGroup = async (
  id: number,
  groupName: string,
  applicationIdsInGroup: string,
  threshold: number = 10
): Promise<MonitorGroup> => {
  return apiFetch<MonitorGroup>(`/groups/${id}`, {
    method: "PUT",
    body: JSON.stringify({ groupName, applicationIdsInGroup, threshold }),
  });
};

const DeleteGroup = async (id: number): Promise<{ success: true }> => {
  return apiFetch<{ success: true }>(`/groups/${id}`, { method: "DELETE" });
};

// --- ADMIN MANAGEMENT ---

const AdminGetAllUsers = async (): Promise<UserProfile[]> => {
  return apiFetch<UserProfile[]>("/admin/users");
};

const AdminAddUser = async (
  email: string,
  role: string = "user",
  enabled: boolean = true
): Promise<UserProfile> => {
  return apiFetch<UserProfile>("/admin/users", {
    method: "POST",
    body: JSON.stringify({ email, role, enabled }),
  });
};

const AdminUpdateUser = async (
  email: string,
  role: string,
  enabled: boolean
): Promise<UserProfile> => {
  return apiFetch<UserProfile>(`/admin/users/${email}`, {
    method: "PUT",
    body: JSON.stringify({ role, enabled }),
  });
};

const AdminDeleteUser = async (email: string): Promise<{ success: true }> => {
  return apiFetch<{ success: true }>(`/admin/users/${email}`, {
    method: "DELETE",
  });
};


// --- ADMIN THRESHOLDS & TAGS ---

/**
 * Updates the global KO threshold percentage
 */
const AdminUpdateGlobalThreshold = async (
  threshold: number
): Promise<{ success: boolean; threshold: number }> => {
  return apiFetch<{ success: boolean; threshold: number }>("/admin/settings/global-threshold", {
    method: "PUT",
    body: JSON.stringify({ threshold }),
  });
};

/**
 * Retrieves the list of tags and their specific thresholds
 * Note: Kept under admin route as requested
 */
const AdminGetTags = async (): Promise<{ tag_name: string; threshold: number; pause_minutes: number }[]> => {
  return apiFetch<{ tag_name: string; threshold: number; pause_minutes: number }[]>("/admin/tags");
};

/**
 * Sets or updates the KO threshold for a specific tag
 */
const AdminSetTagThreshold = async (
  tagName: string,
  threshold: number,
  pauseMinutes: number
): Promise<{ success: boolean }> => {
  return apiFetch<{ success: boolean }>("/admin/tags", {
    method: "POST",
    body: JSON.stringify({ tagName, threshold, pauseMinutes }),
  });
};

/**
 * Retrieves the current global KO threshold percentage
 */
const AdminGetGlobalThreshold = async (): Promise<{ threshold: number }> => {
  return apiFetch<{ threshold: number }>("/admin/settings/global-threshold");
};

/**
 * Removes a tag configuration from the system
 */
const AdminDeleteTag = async (
  tagName: string
): Promise<{ success: boolean }> => {
  return apiFetch<{ success: boolean }>(`/admin/tags/${encodeURIComponent(tagName)}`, {
    method: "DELETE",
  });
};

// DEV API

const DevSendPush = async (
  title: string,
  body: string,
  data?: Record<string, string>
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>("/dev/push", {
    method: "POST",
    body: JSON.stringify({ title, body, data }),
  });
};

/**
 * Retrieves the global notification pause timeout in minutes
 */
const AdminGetNotificationPause = async (): Promise<{ pauseMinutes: number }> => {
  return apiFetch<{ pauseMinutes: number }>("/admin/settings/notification-pause");
};

/**
 * Updates the global notification pause timeout in minutes
 */
const AdminUpdateNotificationPause = async (
  pauseMinutes: number
): Promise<{ success: boolean; pauseMinutes: number }> => {
  return apiFetch<{ success: boolean; pauseMinutes: number }>("/admin/settings/notification-pause", {
    method: "PUT",
    body: JSON.stringify({ pauseMinutes }),
  });
};

const AdminGetGroupNotificationPause = async (): Promise<{ pauseMinutes: number }> => {
  return apiFetch<{ pauseMinutes: number }>("/admin/settings/group-notification-pause");
};

const AdminUpdateGroupNotificationPause = async (
  pauseMinutes: number
): Promise<{ success: boolean; pauseMinutes: number }> => {
  return apiFetch<{ success: boolean; pauseMinutes: number }>("/admin/settings/group-notification-pause", {
    method: "PUT",
    body: JSON.stringify({ pauseMinutes }),
  });
};

const APIService = {
  AdminUpdateGroupNotificationPause,
  AdminGetGroupNotificationPause,
  ValidateOAuth2Token,
  AdminGetNotificationPause,
  AdminUpdateNotificationPause,
  StartUidLogin,
  GetMyProfile,
  UpdateNotificationSettings,
  RegisterFCMToken,
  GetMonitoredServicesStatus,
  GetMonitoredServicesTrend,
  SetIsFavorited,
  GetAllFavorited,
  GetGroups,
  CreateGroup,
  UpdateGroup,
  DeleteGroup,
  AdminGetAllUsers,
  AdminAddUser,
  AdminUpdateUser,
  AdminDeleteUser,
  AdminUpdateGlobalThreshold,
  AdminGetGlobalThreshold,
  AdminGetTags,
  AdminDeleteTag,
  AdminSetTagThreshold,
  DevSendPush
};

/**
 * Demo-aware facade.
 *
 * At call time, if a demo session is active, any method that MockApiService
 * implements is served from the mock (localStorage + static data) instead of the
 * real one — so NOTHING hits the backend while logged in as the demo user.
 * When the demo flag is absent, calls pass straight through to the real service.
 *
 * This is the single point that makes every existing consumer of APIService
 * demo-aware without any change to those files.
 */
const DemoAwareAPIService: typeof APIService = new Proxy(APIService, {
  get(target, prop, receiver) {
    if (
      isDemoSession() &&
      Object.prototype.hasOwnProperty.call(MockApiService, prop)
    ) {
      return (MockApiService as Record<string | symbol, unknown>)[prop];
    }
    return Reflect.get(target, prop, receiver);
  },
});

export default DemoAwareAPIService;